import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  AlertCircle, 
  TrendingUp, 
  Users, 
  Calendar, 
  Clock,
  ChevronRight,
  Plus,
  UserPlus,
  CalendarPlus,
  DollarSign,
  MessageSquare,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Building2,
  ClipboardList
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAgency } from '../../contexts/AgencyContext';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Home Workspace — The Operational Command Center
 * 
 * Design Principles:
 * - 10-Second Rule: User understands where they are, what needs attention, and what to do next
 * - One-Click Rule: Daily tasks are immediately accessible
 * - Workspace Principle: Organized around work, not modules
 * 
 * Four Questions This Page Answers:
 * 1. What needs my attention today?
 * 2. What's happening across my organization?
 * 3. What should I do next?
 * 4. How can Leynix help me complete it?
 */

export default function HomeWorkspace() {
  const { user } = useAuth();
  const { agency } = useAgency();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    attentionItems: [],
    snapshot: null,
    recommendedAction: null,
  });

  const firstName = user?.name?.split(' ')[0] || 'there';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch attention items, snapshot, and recommendations in parallel
      const [exceptionsRes, snapshotRes] = await Promise.all([
        fetch(`${API_URL}/api/exceptions?status=pending&limit=5`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/snapshot/current`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => ({ ok: false })),
      ]);

      const attentionItems = [];
      
      // Process exceptions
      if (exceptionsRes.ok) {
        const exceptions = await exceptionsRes.json();
        if (exceptions?.items?.length > 0) {
          attentionItems.push({
            id: 'exceptions',
            type: 'warning',
            title: `${exceptions.items.length} operational exception${exceptions.items.length > 1 ? 's' : ''} need review`,
            link: '/operations/exceptions',
            priority: 'high'
          });
        }
      }

      // Process snapshot
      let snapshot = null;
      if (snapshotRes.ok) {
        snapshot = await snapshotRes.json();
      }

      // Generate recommended action based on current state
      const recommendedAction = generateRecommendedAction(attentionItems, snapshot);

      setData({
        attentionItems,
        snapshot,
        recommendedAction,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendedAction = (attentionItems, snapshot) => {
    // Priority 1: Handle urgent exceptions
    if (attentionItems.some(item => item.priority === 'high')) {
      return {
        title: 'Review operational exceptions',
        description: 'There are items that need your attention before they impact operations.',
        action: 'Review Now',
        link: '/operations/exceptions',
        icon: AlertTriangle,
        color: 'amber'
      };
    }

    // Priority 2: Check today's schedule
    return {
      title: "Review today's operations",
      description: "Start your day by reviewing scheduled visits and team availability.",
      action: 'View Schedule',
      link: '/operations/scheduling',
      icon: Calendar,
      color: 'blue'
    };
  };

  // Quick Actions - One-Click Rule
  const quickActions = [
    { label: 'Add Client', icon: UserPlus, link: '/organization/data-entry?mode=client', color: 'bg-blue-500' },
    { label: 'Add Caregiver', icon: UserPlus, link: '/organization/data-entry?mode=caregiver', color: 'bg-emerald-500' },
    { label: 'Schedule Visit', icon: CalendarPlus, link: '/operations/scheduling', color: 'bg-purple-500' },
    { label: 'Run Payroll', icon: DollarSign, link: '/business/payroll', color: 'bg-amber-500' },
    { label: 'Send Message', icon: MessageSquare, link: '/operations/communications', color: 'bg-pink-500' },
    { label: 'Ask Leynix', icon: Sparkles, action: 'ask-leynix', color: 'bg-gradient-to-r from-[#176DFF] to-[#29C0FF]' },
  ];

  const handleQuickAction = (action) => {
    if (action.action === 'ask-leynix') {
      // Trigger Ask Leynix palette
      const event = new KeyboardEvent('keydown', {
        key: 'k',
        metaKey: true,
        bubbles: true
      });
      document.dispatchEvent(event);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#176DFF] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-[#64748B]">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* ═══════════════════════════════════════════════════════════════════
            GREETING & CONTEXT
            Immediately tells the user where they are and sets context
        ═══════════════════════════════════════════════════════════════════ */}
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-[#0D1B33] mb-1">
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-[#64748B]">
            Here&apos;s what&apos;s happening at {agency?.name || 'your organization'} today.
          </p>
        </header>

        {/* ═══════════════════════════════════════════════════════════════════
            CAREHUB HERO — Primary Action for Pilot
            Prominent entry point for data entry
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-6">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Users className="w-7 h-7" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">CareHub</h2>
                  <p className="text-emerald-100 text-sm">Add and manage your caregivers & clients</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/organization/data-entry?mode=caregiver">
                  <Button 
                    className="bg-white text-emerald-700 hover:bg-emerald-50 font-medium"
                    data-testid="hero-add-caregiver"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Caregiver
                  </Button>
                </Link>
                <Link to="/organization/data-entry?mode=client">
                  <Button 
                    className="bg-emerald-700 hover:bg-emerald-800 text-white font-medium"
                    data-testid="hero-add-client"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Client
                  </Button>
                </Link>
                <Link to="/organization/data-entry?mode=intake">
                  <Button 
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 font-medium"
                    data-testid="hero-new-intake"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    New Client Intake
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* ═══════════════════════════════════════════════════════════════════
              LEFT COLUMN — Attention + Recommended Action
              Answers: What needs attention? What should I do next?
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Needs Attention */}
            <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <h2 className="font-semibold text-[#0D1B33]">Needs Attention</h2>
                </div>
                {data.attentionItems.length > 0 && (
                  <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    {data.attentionItems.length} item{data.attentionItems.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="p-5">
                {data.attentionItems.length === 0 ? (
                  <div className="flex items-center gap-3 text-emerald-600">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm font-medium">All clear — nothing needs immediate attention</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {data.attentionItems.map((item) => (
                      <Link
                        key={item.id}
                        to={item.link}
                        className="flex items-center justify-between p-3 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-[#0D1B33]">{item.title}</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#64748B] group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Recommended Next Step */}
            {data.recommendedAction && (
              <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E2E8F0]">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#176DFF]" />
                    <h2 className="font-semibold text-[#0D1B33]">Recommended Next Step</h2>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      data.recommendedAction.color === 'amber' ? 'bg-amber-100' : 'bg-blue-100'
                    }`}>
                      <data.recommendedAction.icon className={`w-5 h-5 ${
                        data.recommendedAction.color === 'amber' ? 'text-amber-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-[#0D1B33] mb-1">{data.recommendedAction.title}</h3>
                      <p className="text-sm text-[#64748B] mb-3">{data.recommendedAction.description}</p>
                      <Link to={data.recommendedAction.link}>
                        <Button size="sm" className="bg-[#176DFF] hover:bg-[#176DFF]/90">
                          {data.recommendedAction.action}
                          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Quick Actions — One-Click Rule */}
            <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E2E8F0]">
                <h2 className="font-semibold text-[#0D1B33]">Quick Actions</h2>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {quickActions.map((action) => (
                    action.link ? (
                      <Link
                        key={action.label}
                        to={action.link}
                        className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#E2E8F0] hover:border-[#176DFF]/30 hover:bg-[#F8FAFC] transition-all group"
                        data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${action.color}`}>
                          <action.icon className="w-5 h-5" />
                        </div>
                        <span className="text-xs font-medium text-[#334155] text-center">{action.label}</span>
                      </Link>
                    ) : (
                      <button
                        key={action.label}
                        onClick={() => handleQuickAction(action)}
                        className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#E2E8F0] hover:border-[#176DFF]/30 hover:bg-[#F8FAFC] transition-all group"
                        data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${action.color}`}>
                          <action.icon className="w-5 h-5" />
                        </div>
                        <span className="text-xs font-medium text-[#334155] text-center">{action.label}</span>
                      </button>
                    )
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* ═══════════════════════════════════════════════════════════════════
              RIGHT COLUMN — Executive Snapshot
              Answers: What's happening across my organization?
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="space-y-6">
            
            {/* Executive Snapshot */}
            <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E2E8F0]">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#176DFF]" />
                  <h2 className="font-semibold text-[#0D1B33]">Organization Snapshot</h2>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <SnapshotStat 
                  icon={Users} 
                  label="Active Caregivers" 
                  value={data.snapshot?.workforce?.active_caregivers || '—'}
                  color="blue"
                />
                <SnapshotStat 
                  icon={Users} 
                  label="Active Clients" 
                  value={data.snapshot?.clients?.active_clients || '—'}
                  color="emerald"
                />
                <SnapshotStat 
                  icon={Calendar} 
                  label="Today's Visits" 
                  value={data.snapshot?.visits_today || '—'}
                  color="purple"
                />
                <SnapshotStat 
                  icon={Clock} 
                  label="Coverage Rate" 
                  value={data.snapshot?.coverage_rate ? `${Math.round(data.snapshot.coverage_rate)}%` : '—'}
                  color="amber"
                />
              </div>
              <div className="px-5 py-3 bg-[#F8FAFC] border-t border-[#E2E8F0]">
                <Link 
                  to="/home/snapshot" 
                  className="text-sm font-medium text-[#176DFF] hover:text-[#176DFF]/80 flex items-center gap-1"
                >
                  View full snapshot
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </section>

            {/* Operational Status */}
            <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E2E8F0]">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#64748B]" />
                  <h2 className="font-semibold text-[#0D1B33]">Operational Status</h2>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                  <div>
                    <p className="text-sm font-medium text-[#0D1B33]">All Systems Operational</p>
                    <p className="text-xs text-[#64748B]">Platform running smoothly</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Ask Leynix CTA */}
            <section 
              className="bg-gradient-to-br from-[#0D1B33] to-[#1a2d4d] rounded-xl p-5 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => {
                const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true });
                document.dispatchEvent(event);
              }}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#176DFF]/20 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-[#29C0FF]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Ask Leynix</h3>
                  <p className="text-sm text-white/70 mb-3">
                    Need help? Ask anything about your operations.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <span className="bg-white/10 px-2 py-1 rounded">⌘K</span>
                    <span>to open anytime</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

// Snapshot stat component
function SnapshotStat({ icon: Icon, label, value, color }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-sm text-[#64748B]">{label}</span>
      </div>
      <span className="text-lg font-semibold text-[#0D1B33]">{value}</span>
    </div>
  );
}
